import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { useSettingsNavigation } from "@/hooks/useSettingsNavigation";
import { CompactLanguageSelector } from "@/lib/components/i18n/language-selector";
import {
  groupSettingsByCategory,
  type SettingsItem,
  useSettingsCategories,
  useSettingsItems,
} from "../hooks/use-settings-items";

/**
 * Flat list of settings categories with their items inside. Extracted from
 * the old `SettingsNavigation` so it can be reused in:
 *   - desktop /salon card (`SettingsNavigation` variant="salon")
 *   - mobile /settings drill-in page (`SettingsIndexPage` on mobile)
 *
 * Each category renders as a header + a list of item buttons. Inline items
 * (appearance, language) render their interactive control inline; the rest
 * navigate to their settings page on click.
 */
export function SettingsCategoryList() {
  const { selectSection } = useSettingsNavigation();
  const navigate = useNavigate();
  const settingsItems = useSettingsItems();
  const categoryInfo = useSettingsCategories();
  const groupedSections = groupSettingsByCategory(settingsItems);

  // Items whose `path` doesn't live under `/settings/...` (e.g. the mobile
  // Customers shortcut) need to navigate to that absolute path instead of
  // the default `/settings/${id}` that `selectSection` builds.
  const handleItemClick = (item: SettingsItem) => {
    if (item.path && !item.path.startsWith("/settings/")) {
      navigate(item.path);
      return;
    }
    selectSection(item.id);
  };

  return (
    <div className="space-y-6">
      {Object.entries(categoryInfo).map(([categoryKey, category]) => {
        const categoryItems = groupedSections[categoryKey] || [];
        if (categoryItems.length === 0) return null;

        return (
          <div key={categoryKey} className="space-y-3">
            <div className="border-b border-border pb-2">
              <h4 className="text-sm font-semibold text-foreground">{category.title}</h4>
            </div>

            <div className="space-y-2">
              {categoryItems.map((item: SettingsItem) => {
                const Icon = item.icon;

                if (item.isInline) {
                  return (
                    <div
                      key={item.id}
                      className="h-auto p-3 rounded-lg hover:bg-muted/50 group w-full border border-transparent"
                    >
                      <div className="flex items-center space-x-4 w-full">
                        <div className="flex-shrink-0">
                          <div className="w-11 h-11 rounded-lg bg-muted flex items-center justify-center group-hover:bg-muted/80 transition-colors">
                            <Icon className="w-5 h-5 text-muted-foreground" />
                          </div>
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <p className="text-base font-medium text-foreground break-words">
                            {item.title}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          {item.id === "appearance" && <ThemeToggle />}
                          {item.id === "language" && <CompactLanguageSelector />}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <Button
                    key={item.id}
                    variant="ghost"
                    onClick={() => handleItemClick(item)}
                    className="h-auto p-3 justify-start hover:bg-muted/50 group w-full"
                  >
                    <div className="flex items-center space-x-4 w-full">
                      <div className="flex-shrink-0">
                        <div className="w-11 h-11 rounded-lg bg-muted flex items-center justify-center group-hover:bg-muted/80 transition-colors">
                          <Icon className="w-5 h-5 text-muted-foreground" />
                        </div>
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-base font-medium text-foreground break-words">
                          {item.title}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground" />
                    </div>
                  </Button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
